import React, { useState, useEffect } from "react";
import { usePlayer } from "@empirica/core/player/classic/react";
import { Button } from "./Button.jsx";
import { savePlayerMeta } from "../utils/savePlayerMeta.js";
import { recordSubmittedIntegrityText } from "../utils/integrityTracking.js";
import { useChatAutoScroll } from "../utils/useChatAutoScroll.js";
import { TaskDetailsPanel } from "./TaskDetailsPanel.jsx";

// The reflection activity (Socratic chat or template questionnaire) is shared
// verbatim between the pre-peer and post-peer timings. Only storage keys,
// integrity labels and the countdown anchor differ; the questions, the chat UI,
// the continue gate, and the copy are the same strings in both timings.

export const PRE_LOBBY_REFLECTION_KEYS = {
  messages: "preLobbyMessages",
  aiTrigger: "preLobbyAiTrigger",
  assistantCompleted: "preLobbyAssistantCompleted",
  assistantCompletedAt: "preLobbyAssistantCompletedAt",
  countdownAnchor: "initialPredictionAt",
  chatMessageStageId: "pre-lobby-assistant",
  templateMessageStageId: "pre-lobby-template",
  templateMainReason: "preLobbyTemplateMainReason",
  templateReasonMatters: "preLobbyTemplateReasonMatters",
  templateOppositeReason: "preLobbyTemplateOppositeReason",
  templateQuestionnaire: "preLobbyTemplateQuestionnaire",
  templateQuestionnaireCompletedAt: "preLobbyTemplateQuestionnaireCompletedAt",
  integrityPhase: "pre-lobby",
  integrityStageId: "pre-lobby",
  chatIntegrityStageName: "pre-lobby-ai-chat",
  chatIntegrityFieldId: "prelobby-ai-chat-input",
  templateIntegrityStageName: "pre-lobby-template-questionnaire",
  templateIntegrityFieldPrefix: "prelobby-template",
  chatMessagesDebugTag: "AI Control/Discuss Condition:1",
  templateResponsesDebugTag: "Template Questionnaire:1",
  templateMessagesDebugTag: "Template Questionnaire:2",
};

export const POST_PEER_REFLECTION_KEYS = {
  messages: "postPeerMessages",
  aiTrigger: "postPeerAiTrigger",
  assistantCompleted: "postPeerAssistantCompleted",
  assistantCompletedAt: "postPeerAssistantCompletedAt",
  countdownAnchor: "postPeerBeliefCompletedAt",
  chatMessageStageId: "post-peer-assistant",
  templateMessageStageId: "post-peer-template",
  templateMainReason: "postPeerTemplateMainReason",
  templateReasonMatters: "postPeerTemplateReasonMatters",
  templateOppositeReason: "postPeerTemplateOppositeReason",
  templateQuestionnaire: "postPeerTemplateQuestionnaire",
  templateQuestionnaireCompletedAt: "postPeerTemplateQuestionnaireCompletedAt",
  integrityPhase: "in-game",
  integrityStageId: "post-peer-reflection",
  chatIntegrityStageName: "post-peer-ai-chat",
  chatIntegrityFieldId: "postpeer-ai-chat-input",
  templateIntegrityStageName: "post-peer-template-questionnaire",
  templateIntegrityFieldPrefix: "postpeer-template",
  chatMessagesDebugTag: "Post-peer Reflection Chat:1",
  templateResponsesDebugTag: "Post-peer Template Questionnaire:1",
  templateMessagesDebugTag: "Post-peer Template Questionnaire:2",
};

const REFLECTION_WAITING_TIME_SECONDS = 420; // 7 minutes

export function calculateReflectionRemainingTime(anchorAt) {
  if (!anchorAt) {
    return REFLECTION_WAITING_TIME_SECONDS;
  }
  const elapsed = Math.floor((Date.now() - new Date(anchorAt)) / 1000);
  return Math.max(REFLECTION_WAITING_TIME_SECONDS - elapsed, 0);
}

export function ReflectionAssistantChat({
  next,
  predictionForOpener,
  taskConfig,
  keys = PRE_LOBBY_REFLECTION_KEYS,
  contextPanel = null,
  beliefReminder = null,
  // Off by default so the pre-lobby callers render exactly as before. The
  // post-peer stage turns it on for both arms.
  showDetailsToggle = false,
}) {
  const player = usePlayer();
  const [showTaskDetails, setShowTaskDetails] = useState(!showDetailsToggle);
  const [inputText, setInputText] = useState("");
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [messages, setMessages] = useState([]);
  const [expectingAiResponse, setExpectingAiResponse] = useState(false);
  const [hasUserParticipated, setHasUserParticipated] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(
    calculateReflectionRemainingTime(player.get(keys.countdownAnchor))
  ); // 7 minutes like assistant stage
  const [forceScrollKey, setForceScrollKey] = useState(0);
  const {
    containerRef: messagesContainerRef,
    endRef: messagesEndRef,
    handleScroll,
  } = useChatAutoScroll({
    itemCount: messages.length,
    typingVisible: isAiTyping,
    forceScrollKey,
  });

  // Listen for AI responses from the server
  useEffect(() => {
    const storedMessages = player.get(keys.messages) || [];
    const currentMessageIds = messages.map(m => m.id);

    // Check for new messages that aren't in our local state yet
    const newMessages = storedMessages.filter(msg => !currentMessageIds.includes(msg.id));

    if (newMessages.length > 0) {
      console.log("[REFLECTION-AI] Received new AI messages:", newMessages.length);
      setMessages(prev => [...prev, ...newMessages]);
      setIsAiTyping(false);
      setExpectingAiResponse(false);
      if (newMessages.some((msg) => !msg.meta?.isAI && msg.role === "user" && msg.subjectId === player.id)) {
        setHasUserParticipated(true);
      }
    }
  }, [player.get(keys.messages), messages]);

  useEffect(() => {
    const timer = setInterval(() => {
      const anchorAt = player.get(keys.countdownAnchor);
      if (!anchorAt) {
        player.set(keys.countdownAnchor, Date.now());
      }
      const remaining = calculateReflectionRemainingTime(anchorAt);

      if (remaining <= 1) {
        // Auto-advance when time runs out
        console.log("[REFLECTION-AI] Time limit reached, auto-advancing");
        handleProceed({ requireConfirmation: false });
        return 0;
      }
      setTimeRemaining(remaining);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Trigger initial AI opener from server (LLM-derived), using stored prediction context
  useEffect(() => {
    const existingMessages = player.get(keys.messages) || [];
    const hasAnyMessages = existingMessages.length > 0;
    const hasUserMessage = existingMessages.some(
      (msg) => !msg.meta?.isAI && msg.role === "user" && msg.subjectId === player.id
    );
    if (hasUserMessage) {
      setHasUserParticipated(true);
    }
    if (!hasAnyMessages && predictionForOpener) {
      setIsAiTyping(true);
      setExpectingAiResponse(true);
      player.set(keys.aiTrigger, Date.now());
      console.log("[REFLECTION-AI] Triggered initial AI opener for player:", player.id);
    }
  }, [predictionForOpener]);

  const handleSendMessage = async () => {
    if (!inputText.trim() || expectingAiResponse) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      text: inputText.trim(),
      createdAt: Date.now(),
      meta: { isAI: false },
      role: "user",
      subjectId: player.id
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText("");
    setExpectingAiResponse(true);
    setIsAiTyping(true);
    setHasUserParticipated(true);
    setForceScrollKey((prev) => prev + 1);

    recordSubmittedIntegrityText({
      playerId: player.id,
      fieldId: keys.chatIntegrityFieldId,
      text: userMessage.text,
      phase: keys.integrityPhase,
      stageId: keys.integrityStageId,
      stageName: keys.chatIntegrityStageName,
    });

    // Store the user message
    const existingMessages = player.get(keys.messages) || [];
    const updatedMessages = [...existingMessages, {
      ...userMessage,
      stageId: keys.chatMessageStageId,
      playerId: player.id
    }];
    player.set(keys.messages, updatedMessages);

    console.log("[REFLECTION-AI] Stored message on player, total messages now:", updatedMessages.length);

    // Small delay to ensure message storage propagates before triggering AI
    setTimeout(() => {
      player.set(keys.aiTrigger, Date.now());
      console.log("[REFLECTION-AI] Triggered AI generation for player:", player.id);
    }, 50);
  };

  const handleProceed = ({ requireConfirmation = true } = {}) => {
    if (requireConfirmation) {
      const confirmed = window.confirm(
        "You are about to finish the conversation with the AI assistant. Please make sure you have finished discussing before continuing."
      );
      if (!confirmed) {
        return;
      }
    }

    // Mark the assistant chat as completed
    player.set(keys.assistantCompleted, true);
    player.set(keys.assistantCompletedAt, Date.now());
    const storedMessages = player.get(keys.messages) || [];
    savePlayerMeta({ player, name: keys.messages, value: storedMessages, debugTag: keys.chatMessagesDebugTag });

    // Advance to the next step
    next();
  };

  const DetailsComponent = taskConfig?.DetailsComponent;

  return (
    <div className="h-full min-h-0 w-full max-w-4xl mx-auto p-6 flex flex-col">
      <h2 className="text-2xl font-bold mb-6 shrink-0">{taskConfig?.aiChatTitle || "AI Assistant Discussion"}</h2>

      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        <div className="space-y-6">
          {taskConfig?.integrityNotice && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-900">{taskConfig.integrityNotice}</p>
            </div>
          )}

          {/* Timer Display */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between gap-4">
              <p className="text-gray-700">
                {taskConfig?.aiChatDescription || "Discuss your prediction with the AI assistant to clarify your thinking before joining the group. It will take 5-10 seconds for AI assistant to respond."}
              </p>
              <div className="text-right shrink-0">
                <div className="text-sm text-gray-500">Time Remaining</div>
                <div className={`text-xl font-mono font-semibold ${timeRemaining < 60 ? 'text-red-600' : 'text-gray-900'}`}>
                  {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
                </div>
              </div>
            </div>
          </div>

          {contextPanel}

          {/* Loan Case Details */}
          <div className="bg-white rounded-lg shadow-md p-6">
            {showDetailsToggle ? (
              <TaskDetailsPanel
                taskConfig={taskConfig}
                open={showTaskDetails}
                onToggle={() => setShowTaskDetails((prev) => !prev)}
                scrollable
              />
            ) : (
              <div className="max-h-72 overflow-y-auto pr-2">
                {DetailsComponent ? <DetailsComponent /> : null}
              </div>
            )}
          </div>

          {beliefReminder}

          {/* Chat Interface */}
          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col min-h-[28rem]">
            <div
              ref={messagesContainerRef}
              className="flex-1 min-h-0 overflow-y-auto mb-4 border rounded-lg p-4 bg-gray-50"
              onScroll={handleScroll}
            >
              {messages.map((message) => (
                <div key={message.id} className={`mb-4 ${message.meta?.isAI ? 'text-left' : 'text-right'}`}>
                  <div className={`inline-block max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    message.meta?.isAI
                      ? 'bg-blue-100 text-blue-900'
                      : 'bg-green-100 text-green-900'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold">
                        {message.meta?.isAI ? '🤖 AI Assistant' : '👤 You'}
                      </span>
                      <span className="text-xs opacity-70">
                        {new Date(message.createdAt).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-sm">{message.text}</p>
                  </div>
                </div>
              ))}
              {isAiTyping && (
                <div className="text-left mb-4">
                  <div className="inline-block px-4 py-2 rounded-lg bg-blue-100 text-blue-900">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold">🤖 AI Assistant</span>
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="flex gap-2 shrink-0">
              <input
                type="text"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-empirica-500 focus:border-empirica-500"
                placeholder="Share your thoughts with the AI assistant..."
                value={inputText}
                data-integrity-field={keys.chatIntegrityFieldId}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                disabled={expectingAiResponse}
              />
              <Button
                handleClick={handleSendMessage}
                disabled={!inputText.trim() || expectingAiResponse}
                className="px-4 py-2 bg-empirica-500 hover:bg-empirica-600 disabled:opacity-50"
              >
                Send
              </Button>
            </div>
          </div>

          {/* AI Response Note */}
          {expectingAiResponse && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> If you do not receive an AI response within 20 seconds, please refresh the browser and resend your message.
              </p>
            </div>
          )}

          {/* Proceed */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4">{taskConfig?.aiChatCompleteHeading || "Discussion Complete?"}</h3>
            <p className="mb-4 text-gray-600">
              {taskConfig?.aiChatCompleteDescription || "When you're satisfied with your discussion with the AI assistant, click below to proceed to confirm your prediction."}
              {" "}The conversation will automatically advance after 7 minutes if you haven't proceeded manually.
            </p>
            <p className="mb-4 text-gray-700">
              Please finish a meaningful conversation with the AI assistant and only proceed when your discussion is complete.
            </p>

            <div className="flex justify-center">
              <Button
                handleClick={handleProceed}
                className="px-8 py-3 bg-green-500 hover:bg-green-600"
                disabled={!hasUserParticipated}
              >
                Continue
              </Button>
            </div>
            {!hasUserParticipated && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                Please send at least one message to the AI assistant first.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ReflectionTemplateQuestionnaire({
  next,
  taskConfig,
  keys = PRE_LOBBY_REFLECTION_KEYS,
  contextPanel = null,
  beliefReminder = null,
  // Off by default so the pre-lobby callers render exactly as before. The
  // post-peer stage turns it on for both arms.
  showDetailsToggle = false,
}) {
  const player = usePlayer();
  const [showTaskDetails, setShowTaskDetails] = useState(!showDetailsToggle);
  const DetailsComponent = taskConfig?.DetailsComponent;
  const defaultTemplateQuestionnaire = {
    introText: null,
    mainReason: {
      question: "What is the main reason behind your prediction?",
      placeholder: "Please describe your main reason.",
    },
    reasonMatters: {
      question: "Could you say more about why you think that reason matters in this scenario?",
      placeholder: "Please elaborate on why this reason is important.",
    },
    oppositeReason: {
      question: "What do you think is the strongest reason someone might predict the opposite?",
      placeholder: "Please describe the strongest argument for the opposite prediction.",
    },
  };
  const taskTemplateQuestionnaire = taskConfig?.templateQuestionnaire || {};
  const templateQuestionnaire = {
    ...defaultTemplateQuestionnaire,
    ...taskTemplateQuestionnaire,
    mainReason: {
      ...defaultTemplateQuestionnaire.mainReason,
      ...(taskTemplateQuestionnaire.mainReason || {}),
    },
    reasonMatters: {
      ...defaultTemplateQuestionnaire.reasonMatters,
      ...(taskTemplateQuestionnaire.reasonMatters || {}),
    },
    oppositeReason: {
      ...defaultTemplateQuestionnaire.oppositeReason,
      ...(taskTemplateQuestionnaire.oppositeReason || {}),
    },
  };
  const [mainReason, setMainReason] = useState(() => player.get(keys.templateMainReason) || "");
  const [reasonMatters, setReasonMatters] = useState(() => player.get(keys.templateReasonMatters) || "");
  const [oppositeReason, setOppositeReason] = useState(() => player.get(keys.templateOppositeReason) || "");
  const trimmedMainReason = mainReason.trim();
  const trimmedReasonMatters = reasonMatters.trim();
  const trimmedOppositeReason = oppositeReason.trim();
  const templateQuestions = [
    {
      idSuffix: "main-reason",
      questionId: "mainReason",
      config: templateQuestionnaire.mainReason,
      rawValue: mainReason,
      value: trimmedMainReason,
      setter: setMainReason,
    },
    {
      idSuffix: "reason-matters",
      questionId: "reasonMatters",
      config: templateQuestionnaire.reasonMatters,
      rawValue: reasonMatters,
      value: trimmedReasonMatters,
      setter: setReasonMatters,
    },
    {
      idSuffix: "opposite-reason",
      questionId: "oppositeReason",
      config: templateQuestionnaire.oppositeReason,
      rawValue: oppositeReason,
      value: trimmedOppositeReason,
      setter: setOppositeReason,
    },
  ];
  const visibleTemplateQuestions = templateQuestions.filter(
    ({ config }) => config?.enabled !== false
  );
  const hasCompletedAllQuestions = visibleTemplateQuestions.every(({ value }) => Boolean(value));

  const handleContinue = () => {
    if (!hasCompletedAllQuestions) {
      alert("Please answer all required questions before continuing.");
      return;
    }

    const timestamp = Date.now();
    const getStoredValue = (questionConfig, value) =>
      questionConfig?.enabled === false ? "" : value;
    const responses = {
      mainReason: getStoredValue(templateQuestionnaire.mainReason, trimmedMainReason),
      reasonMatters: getStoredValue(templateQuestionnaire.reasonMatters, trimmedReasonMatters),
      oppositeReason: getStoredValue(templateQuestionnaire.oppositeReason, trimmedOppositeReason),
      completedAt: timestamp,
    };

    player.set(keys.templateMainReason, responses.mainReason);
    player.set(keys.templateReasonMatters, responses.reasonMatters);
    player.set(keys.templateOppositeReason, responses.oppositeReason);
    player.set(keys.templateQuestionnaireCompletedAt, timestamp);
    player.set(keys.templateQuestionnaire, responses);

    const makeTemplateMessage = (idSuffix, questionId, questionText, answerText, createdAtOffset) => ({
      id: `template-${idSuffix}-${timestamp}`,
      role: "user",
      text: `Question: ${questionText}\nAnswer: ${answerText}`,
      createdAt: timestamp + createdAtOffset,
      subjectId: player.id,
      stageId: keys.templateMessageStageId,
      playerId: player.id,
      meta: {
        isAI: false,
        templateQuestionnaire: true,
        questionId,
      },
    });

    const templateMessages = visibleTemplateQuestions.map(
      ({ idSuffix, questionId, config, value }, index) =>
        makeTemplateMessage(idSuffix, questionId, config.question, value, index)
    );

    visibleTemplateQuestions.forEach(({ questionId, value }) => {
      recordSubmittedIntegrityText({
        playerId: player.id,
        fieldId: `${keys.templateIntegrityFieldPrefix}-${questionId}`,
        text: value,
        phase: keys.integrityPhase,
        stageId: keys.integrityStageId,
        stageName: keys.templateIntegrityStageName,
      });
    });

    const existingMessages = (player.get(keys.messages) || []).filter(
      (msg) => !msg?.meta?.templateQuestionnaire
    );
    const updatedMessages = [...existingMessages, ...templateMessages];
    player.set(keys.messages, updatedMessages);

    savePlayerMeta({
      player,
      name: keys.templateQuestionnaire,
      value: responses,
      debugTag: keys.templateResponsesDebugTag,
    });
    savePlayerMeta({
      player,
      name: keys.messages,
      value: updatedMessages,
      debugTag: keys.templateMessagesDebugTag,
    });

    next();
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Follow-up Questionnaire</h2>

      {taskConfig?.integrityNotice && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-900">{taskConfig.integrityNotice}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <p className="text-gray-700">
          {templateQuestionnaire.introText ||
            `Before confirming your prediction for ${
              taskConfig?.shortTitle || "this scenario"
            }, please answer the questions below.`}
        </p>
      </div>

      {contextPanel}

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        {showDetailsToggle ? (
          <TaskDetailsPanel
            taskConfig={taskConfig}
            open={showTaskDetails}
            onToggle={() => setShowTaskDetails((prev) => !prev)}
          />
        ) : DetailsComponent ? (
          <DetailsComponent />
        ) : null}
      </div>

      {beliefReminder}

      {visibleTemplateQuestions.map(({ questionId, config, rawValue, setter }) => (
        <div key={questionId} className="bg-white rounded-lg shadow-md p-6 mb-6">
          <label className="block text-lg font-semibold mb-2">
            {config.question}
          </label>
          <textarea
            className="w-full border border-gray-300 rounded-md p-3 min-h-[120px]"
            value={rawValue}
            data-integrity-field={`${keys.templateIntegrityFieldPrefix}-${questionId}`}
            onChange={(e) => setter(e.target.value)}
            placeholder={config.placeholder}
          />
        </div>
      ))}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center">
          <Button
            handleClick={handleContinue}
            className="px-8 py-3 bg-green-500 hover:bg-green-600"
            disabled={!hasCompletedAllQuestions}
          >
            Continue
          </Button>
        </div>
        {!hasCompletedAllQuestions && (
          <p className="text-sm text-gray-500 mt-2 text-center">
            Please answer all required questions before continuing.
          </p>
        )}
      </div>
    </div>
  );
}
